import { Editor as CodeEditor, OnMount } from '@monaco-editor/react'
import { Box, Button, Divider, FormControl, FormHelperText, Typography } from '@mui/material'
import { editor } from 'monaco-editor'
import { useRef } from 'react'
import { Controller, FieldPath, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import Loading from 'components/Loading'
import Tooltip from 'components/Tooltip'
import { CodeFieldContainer } from './CodeFieldContainer'
import { CODE_FIELD_COMMON_OPTIONS } from './constants'
import { CodeFieldProps } from './types'
import { useResizable } from './useResizable'

export const CodeField = <T extends FieldValues>({
  control,
  disabled,
  height,
  language,
  name,
  options,
  title,
  tooltip: tooltipProps,
}: CodeFieldProps<T>): JSX.Element => {
  const { t } = useTranslation('common')
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
  const { containerRef, editorHeight, headerRef } = useResizable({ defaultHeight: height })

  const handleOnMount: OnMount = editor => {
    editorRef.current = editor
  }

  const formatEditor = (editor: editor.IStandaloneCodeEditor): void => {
    editor.getAction('editor.action.formatDocument')?.run()
  }

  const Header: JSX.Element = (
    <Box ref={headerRef}>
      <FlexBox axis='x'>
        <Typography sx={{ ml: 2 }}>{title}</Typography>
        {tooltipProps && <Tooltip {...tooltipProps} />}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          disabled={disabled}
          onClick={(): void => {
            if (editorRef.current) {
              formatEditor(editorRef.current)
            }
          }}
          size='small'
          sx={{ m: 1 }}
          variant='outlined'
        >
          {t('form.action.format')}
        </Button>
      </FlexBox>

      <Divider />
    </Box>
  )

  return (
    <Controller
      control={control}
      name={name as FieldPath<T>}
      render={({ field: { onChange, value }, fieldState: { error } }): JSX.Element => (
        <FormControl error={Boolean(error)}>
          <CodeFieldContainer
            error={error}
            ref={containerRef}
          >
            {Header}

            <Box sx={{ height: editorHeight }}>
              <CodeEditor
                language={language}
                loading={<Loading />}
                onChange={onChange}
                onMount={handleOnMount}
                options={{
                  ...options,
                  ...CODE_FIELD_COMMON_OPTIONS,
                  readOnly: disabled,
                }}
                value={value}
                wrapperProps={{
                  'data-cy': `code-editor-${name}`,
                }}
              />
            </Box>
          </CodeFieldContainer>

          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}
